import { EditOutlined, SendOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import { Button, Table, Tag, Tooltip, message, Space } from "antd";
import axios from "axios";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import useDarkModeClass from "../../hooks/useDarkModeClass";
import createThemedSwal from "../../utils/sweetAlertWithTheme";
import { getOrderStatusInfo, getShippingCompanyInfo } from "./orderConstants";

const OrderTable = ({ myData, hideFields, onPageChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = useDarkModeClass();
  const themedSwal = createThemedSwal(isDarkMode);

  const handelDelete = (id) => {
    themedSwal
      .fire({
        title: t("orders.confirmDelete", "Do you want to delete this order?"),
        showCancelButton: true,
        confirmButtonText: t("orders.yesDelete", "Yes, delete"),
      })
      .then((result) => {
        if (result.isConfirmed) {
          axios
            .delete(`/order/api/v1/orders/admin/${id}/`)
            .then(() => {
              Swal.fire(t("orders.deleted", "Deleted!"), "", "success");
              const currentPage = myData.current_page;
              if (myData.results.length === 1 && currentPage > 1) {
                onPageChange(currentPage - 1);
              } else {
                onPageChange(currentPage);
              }
            })
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text: err?.response?.data?.message || err.message,
              });
            });
        }
      });
  };

  const handleAutoOrder = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const { data } = await axios.post("/alibaba/api/v1/crossOrder/", {
              wege_order_id: id,
            });
            themedSwal.fire({
              icon: data?.failed_orders ? "error" : "success",
              title: data?.failed_orders ? "Oops..." : "success",
              text: data?.failed_orders
                ? data.failed_orders[0]?.message
                : data.message,
            });
          } catch (err) {
            themedSwal.fire({
              icon: "error",
              title: "Oops...",
              text: err?.response?.data?.message || err.message,
            });
          }
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const columns = [
    {
      title: t("table.columns.orderId", "Order ID"),
      dataIndex: "id",
      key: "id",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Link
            to={`/orders/admin/${text}`}
            title={`Order #${record?.order_number || text}`}
            style={{
              fontWeight: 600,
              color: "#1677ff",
              textDecoration: "none",
            }}
          >
            #{text}
          </Link>
          {text && (
            <Tooltip title="Copy ID">
              <CopyOutlined
                style={{ color: "#1677ff", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(String(text));
                  message.success("ID copied!");
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("table.columns.orderNo", "Order No."),
      dataIndex: "order_number",
      key: "order_number",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {text ? (
            <Link
              to={`/orders/admin/${record?.id}`}
              title={`Order #${text}`}
              style={{
                fontWeight: 600,
                color: "#1677ff",
                textDecoration: "none",
              }}
            >
              {text}
            </Link>
          ) : (
            "-"
          )}
          {text && (
            <Tooltip title="Copy Order Number">
              <CopyOutlined
                style={{ color: "#1677ff", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(String(text));
                  message.success("Order Number copied!");
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("table.columns.phone", "Phone"),
      dataIndex: "phone1",
      key: "phone1",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {text || "-"}
          {text && (
            <Tooltip title="Copy Phone">
              <CopyOutlined
                style={{ color: "#1677ff", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(String(text));
                  message.success("Phone copied!");
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("table.columns.shippingCo", "Shipping Co."),
      dataIndex: "shipping_company",
      key: "shipping_company",
      render: (text) => {
        const shipInfo = getShippingCompanyInfo(text);
        return shipInfo ? (
          <Tag color={shipInfo.color}>{shipInfo.name}</Tag>
        ) : (
          text || "-"
        );
      },
    },
    {
      title: t("table.columns.date", "Date"),
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => {
        try {
          const date = new Date(text);
          return isNaN(date)
            ? ""
            : date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
        } catch {
          return "";
        }
      },
    },
    {
      title: t("table.columns.status", "Status"),
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const statusInfo = getOrderStatusInfo(text, t);
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: t("table.columns.payment", "Payment"),
      dataIndex: "is_paid",
      key: "is_paid",
      render: (isPaid, record) => (
        <div>
          <Tag color={isPaid ? "success" : "error"}>
            {isPaid
              ? t("table.common.paid", "Paid")
              : t("table.common.unpaid", "Unpaid")}
          </Tag>
          {record.payment_method && (
            <span style={{ fontSize: "12px", color: "#888", marginLeft: "4px" }}>
              {record.payment_method}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t("table.columns.total", "Total"),
      dataIndex: "total",
      key: "total",
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    {
      title: t("table.columns.actions", "Actions"),
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t("table.actions.autoOrder", "Auto Order")}>
            <Button
              type="primary"
              size="small"
              style={{ background: "#13c2c2", borderColor: "#13c2c2" }}
              icon={<SendOutlined />}
              onClick={() => handleAutoOrder(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("table.actions.edit", "Edit Order")}>
            <Link to={`/orders/admin/${record.id}`}>
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
              />
            </Link>
          </Tooltip>
          <Tooltip title={t("table.actions.delete", "Delete Order")}>
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handelDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Table
        className={isDarkMode ? "dark-ant-table" : ""}
        columns={columns}
        dataSource={myData.results}
        rowKey="id"
        pagination={{
          current: myData.current_page,
          pageSize: myData.page_size,
          total: myData.count,
          onChange: onPageChange,
          showSizeChanger: false,
          showQuickJumper: true,
        }}
      />
    </div>
  );
};

export default OrderTable;
